import React, { useState } from 'react';

// A match is live when its panel match is (match_status from the bracket GET, linked_status from an
// older route, status for very old rows). Live = red everywhere, like the other bracket formats.
const isMatchLive = (m) => m.status === 'live' || m.linked_status === 'live' || m.match_status === 'live';
const LIVE = { text: '#ef4444', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.4)', badge: 'rgba(239,68,68,0.15)' };

function GroupStageView({ bracketData, bracketId, bracketStatus, onSetWinner, onResetMatch, onScheduleMatch, onUpdateGroupBo, onUpdateGroupFormat, onAddTeamToGroup, onRemoveTeamFromGroup, onSwapGroupTeams, onSwapTeams, onGenerateMatches, onAddGroup, onDeleteGroup, onUpdateMatchBo, onSwapMatchTeams, allTeams, servers, readOnly, matchLinkPrefix }) {
  const { matches = [], teams = {}, groups = [] } = bracketData || {};
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [addingTeam, setAddingTeam] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [movingTeam, setMovingTeam] = useState(null); // { teamId, fromGroupId }
  const [scheduleModal, setScheduleModal] = useState(null);
  const [schedServerId, setSchedServerId] = useState('');

  // Drag-and-drop state for team reordering within groups (seeds)
  const [dragSource, setDragSource] = useState(null); // { groupId, teamId }
  const [dragOverTarget, setDragOverTarget] = useState(null); // { groupId, teamId }

  // Drag-and-drop state for swapping teams across match cards
  const [matchDragSource, setMatchDragSource] = useState(null); // { matchId, slot, teamId, round, position, side }
  const [matchDragOver, setMatchDragOver] = useState(null); // { matchId, slot }

  const teamName = (id) => teams[id]?.name || `Team #${id}`;
  const isDraft = bracketStatus === 'draft';
  const isActive = bracketStatus === 'active';

  const assignedTeamIds = new Set();
  groups.forEach(g => (g.standings || []).forEach(s => assignedTeamIds.add(s.team_id)));
  const availableTeams = (allTeams || []).filter(t => !assignedTeamIds.has(t.id));

  const hasMatches = groups.some(g => (g.matches || []).length > 0);

  const statusStyle = (status) => {
    const map = {
      pending: { bg: 'rgba(100,116,139,0.06)', border: 'rgba(100,116,139,0.15)', text: 'var(--text-muted)' },
      scheduled: { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.3)', text: '#22d3ee' },
      live: { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.4)', text: '#34d399' },
      completed: { bg: 'rgba(6,182,212,0.06)', border: 'rgba(6,182,212,0.25)', text: '#67e8f9' },
      bye: { bg: 'rgba(100,116,139,0.03)', border: 'rgba(100,116,139,0.08)', text: 'var(--text-muted)' },
    };
    return map[status] || map.pending;
  };

  if (!groups || groups.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No groups found.</div>;
  }

  const renderMatchCard = (m) => {
    const sc = statusStyle(m.status);
    // the bracket GET sends the panel match's status as match_status (linked_status = an older route)
    const isLive = isMatchLive(m);
    const isCompleted = m.status === 'completed';
    const isBye = m.status === 'bye';
    const isScheduled = m.status === 'scheduled' || m.linked_status === 'started' || m.linked_status === 'live';
    const canAct = !readOnly && isActive;
    const canDragMatch = !readOnly && (isDraft || isActive) && m.status === 'pending' && !m.match_id;
    const linkPrefix = matchLinkPrefix || '/admin/matches';
    const matchLink = m.match_id ? `${linkPrefix}/${m.match_id}` : null;

    // Use linked match scores if available
    const effectiveBestOf = m.match_best_of || m.best_of || 1;
    const isMultiMap = effectiveBestOf > 1;
    const s1 = isMultiMap ? (m.match_map_wins_team1 ?? 0) : (m.match_score_team1 ?? 0);
    const s2 = isMultiMap ? (m.match_map_wins_team2 ?? 0) : (m.match_score_team2 ?? 0);
    const hasScore = isLive || isCompleted || isScheduled;

    const card = (
      <div key={m.id} style={{
        background: isLive ? LIVE.bg : sc.bg,
        border: `1px solid ${isLive ? LIVE.border : sc.border}`,
        borderRadius: 'var(--radius-md)', overflow: 'hidden',
        cursor: matchLink ? 'pointer' : 'default',
        transition: 'all 0.15s',
      }}>
        {/* Team 1 row */}
        <div
          draggable={canDragMatch && !!m.team1_id}
          onDragStart={canDragMatch && m.team1_id ? (e) => {
            e.stopPropagation();
            setMatchDragSource({ matchId: m.id, slot: 'team1', teamId: m.team1_id, round: m.round_number, position: m.position, side: m.bracket_side || 'winners' });
            e.dataTransfer.effectAllowed = 'move';
          } : undefined}
          onDragOver={canDragMatch ? (e) => { e.preventDefault(); e.stopPropagation(); setMatchDragOver({ matchId: m.id, slot: 'team1' }); } : undefined}
          onDragLeave={canDragMatch ? () => setMatchDragOver(null) : undefined}
          onDrop={canDragMatch ? (e) => {
            e.preventDefault(); e.stopPropagation();
            if (matchDragSource && (matchDragSource.matchId !== m.id || matchDragSource.slot !== 'team1')) {
              onSwapTeams && onSwapTeams(matchDragSource.round, matchDragSource.position, matchDragSource.slot, m.round_number, m.position, 'team1', matchDragSource.side, m.bracket_side || 'winners');
            }
            setMatchDragSource(null); setMatchDragOver(null);
          } : undefined}
          onDragEnd={() => { setMatchDragSource(null); setMatchDragOver(null); }}
          style={{
            display: 'flex', alignItems: 'center', padding: '5px 10px', gap: '5px',
            borderBottom: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
            background: matchDragOver && matchDragOver.matchId === m.id && matchDragOver.slot === 'team1'
              ? 'rgba(6,182,212,0.2)' : (isCompleted && m.winner_id === m.team1_id ? 'rgba(34,197,94,0.06)' : 'transparent'),
            cursor: canDragMatch && m.team1_id ? 'grab' : 'default',
            transition: 'background 0.15s',
          }}>
          {canDragMatch && m.team1_id && <span style={{ fontSize: '0.5rem', color: 'var(--text-muted)' }}>⠿</span>}
          <span style={{
            flex: 1, fontSize: '0.74rem', fontWeight: isCompleted && m.winner_id === m.team1_id ? 700 : 600,
            color: isCompleted && m.winner_id === m.team1_id ? '#34d399' : isCompleted && m.winner_id && m.winner_id !== m.team1_id ? 'var(--text-muted)' : 'var(--text-primary, #f1f5f9)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            opacity: isCompleted && m.winner_id && m.winner_id !== m.team1_id ? 0.5 : 1,
          }}>
            {m.team1_id ? teamName(m.team1_id) : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.78rem' }}>TBD</span>}
          </span>
          <span style={{
            fontFamily: 'monospace', fontWeight: 800, fontSize: '0.78rem', minWidth: '18px', textAlign: 'center',
            color: isLive ? LIVE.text : (isCompleted && m.winner_id === m.team1_id ? '#34d399' : 'var(--text-secondary)'),
          }}>
            {hasScore ? s1 : ''}
          </span>
        </div>

        {/* Team 2 row */}
        <div
          draggable={canDragMatch && !!m.team2_id}
          onDragStart={canDragMatch && m.team2_id ? (e) => {
            e.stopPropagation();
            setMatchDragSource({ matchId: m.id, slot: 'team2', teamId: m.team2_id, round: m.round_number, position: m.position, side: m.bracket_side || 'winners' });
            e.dataTransfer.effectAllowed = 'move';
          } : undefined}
          onDragOver={canDragMatch ? (e) => { e.preventDefault(); e.stopPropagation(); setMatchDragOver({ matchId: m.id, slot: 'team2' }); } : undefined}
          onDragLeave={canDragMatch ? () => setMatchDragOver(null) : undefined}
          onDrop={canDragMatch ? (e) => {
            e.preventDefault(); e.stopPropagation();
            if (matchDragSource && (matchDragSource.matchId !== m.id || matchDragSource.slot !== 'team2')) {
              onSwapTeams && onSwapTeams(matchDragSource.round, matchDragSource.position, matchDragSource.slot, m.round_number, m.position, 'team2', matchDragSource.side, m.bracket_side || 'winners');
            }
            setMatchDragSource(null); setMatchDragOver(null);
          } : undefined}
          onDragEnd={() => { setMatchDragSource(null); setMatchDragOver(null); }}
          style={{
            display: 'flex', alignItems: 'center', padding: '5px 10px', gap: '5px',
            background: matchDragOver && matchDragOver.matchId === m.id && matchDragOver.slot === 'team2'
              ? 'rgba(6,182,212,0.2)' : (isCompleted && m.winner_id === m.team2_id ? 'rgba(34,197,94,0.06)' : 'transparent'),
            cursor: canDragMatch && m.team2_id ? 'grab' : 'default',
            transition: 'background 0.15s',
          }}>
          {canDragMatch && m.team2_id && <span style={{ fontSize: '0.5rem', color: 'var(--text-muted)' }}>⠿</span>}
          <span style={{
            flex: 1, fontSize: '0.74rem', fontWeight: isCompleted && m.winner_id === m.team2_id ? 700 : 600,
            color: isCompleted && m.winner_id === m.team2_id ? '#34d399' : isCompleted && m.winner_id && m.winner_id !== m.team2_id ? 'var(--text-muted)' : 'var(--text-primary, #f1f5f9)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            opacity: isCompleted && m.winner_id && m.winner_id !== m.team2_id ? 0.5 : 1,
          }}>
            {m.team2_id ? teamName(m.team2_id) : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.78rem' }}>TBD</span>}
          </span>
          <span style={{
            fontFamily: 'monospace', fontWeight: 800, fontSize: '0.78rem', minWidth: '18px', textAlign: 'center',
            color: isLive ? LIVE.text : (isCompleted && m.winner_id === m.team2_id ? '#34d399' : 'var(--text-secondary)'),
          }}>
            {hasScore ? s2 : ''}
          </span>
        </div>

        {/* Actions bar */}
        {(canAct || isLive || m.match_id) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '3px 10px', gap: '4px',
            background: 'rgba(0,0,0,0.15)', borderTop: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {isLive && (
                <span style={{
                  fontSize: '0.5rem', fontWeight: 800, color: LIVE.text,
                  background: LIVE.badge, padding: '2px 6px', borderRadius: 3,
                  display: 'flex', alignItems: 'center', gap: '3px',
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: LIVE.text, animation: 'bracketPulse 1.5s ease-in-out infinite' }} />
                  LIVE
                </span>
              )}
              {m.match_id && (
                <span style={{ fontSize: '0.5rem', color: '#06b6d4', fontWeight: 600 }}>#{m.match_id}</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {canAct && m.status === 'pending' && m.team1_id && m.team2_id && (
                <>
                  <button onClick={e => { e.stopPropagation(); e.preventDefault(); onSetWinner && onSetWinner(m.id, m.team1_id); }}
                    style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#34d399', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>W1</button>
                  <button onClick={e => { e.stopPropagation(); e.preventDefault(); onSetWinner && onSetWinner(m.id, m.team2_id); }}
                    style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)', color: '#22d3ee', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>W2</button>
                  <button onClick={e => { e.stopPropagation(); e.preventDefault(); setScheduleModal(m); }}
                    style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)', color: '#67e8f9', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>Sched</button>
                </>
              )}
              {canAct && (m.status === 'pending' || m.status === 'scheduled') && m.team1_id && m.team2_id && (
                <button onClick={e => { e.stopPropagation(); e.preventDefault(); onSwapMatchTeams && onSwapMatchTeams(m.id); }}
                  title="Swap team sides"
                  style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>⇄</button>
              )}
              {canAct && (m.status === 'pending' || m.status === 'scheduled') && (
                <select
                  value={m.best_of || 1}
                  onClick={e => e.stopPropagation()}
                  onChange={e => { e.stopPropagation(); onUpdateMatchBo && onUpdateMatchBo(m.id, parseInt(e.target.value)); }}
                  style={{ padding: '1px 4px', background: 'rgba(12,12,24,0.55)', border: '1px solid rgba(6,182,212,0.2)', borderRadius: 4, color: '#67e8f9', fontSize: '0.52rem', fontWeight: 700, outline: 'none', cursor: 'pointer' }}
                >
                  <option value={1}>BO1</option>
                  <option value={3}>BO3</option>
                  <option value={5}>BO5</option>
                </select>
              )}
              {canAct && isCompleted && (
                <button onClick={e => { e.stopPropagation(); e.preventDefault(); onResetMatch && onResetMatch(m.id); }}
                  style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.2)', color: '#fb7185', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>Reset</button>
              )}
            </div>
          </div>
        )}
      </div>
    );

    // Wrap in link if match is scheduled
    if (matchLink && !readOnly) {
      return <div key={m.id} onClick={() => window.location.href = matchLink} style={{ cursor: 'pointer' }}>{card}</div>;
    }
    return <React.Fragment key={m.id}>{card}</React.Fragment>;
  };

  return (
    <div style={{ padding: '1rem', overflowY: 'auto', height: '100%' }}>
      {/* Top controls */}
      {!readOnly && isDraft && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={onAddGroup} style={{
            background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)',
            color: '#67e8f9', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
          }}>+ Add Group</button>
          {groups.some(g => (g.standings || []).length >= 2) && (
            <button onClick={onGenerateMatches} style={{
              background: 'linear-gradient(135deg, #16a34a, #34d399)', border: 'none',
              color: '#fff', padding: '0.4rem 1rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
              boxShadow: '0 4px 14px rgba(34,197,94,0.3)',
            }}>
              {hasMatches ? 'Regenerate Matches' : 'Generate Matches'}
            </button>
          )}
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {assignedTeamIds.size} teams assigned | {availableTeams.length} available
          </span>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(groups.length, 2)}, 1fr)`,
        gap: '1rem',
      }}>
        {groups.map((group, gIdx) => {
          const isExpanded = expandedGroup === gIdx || true; // Always show matches in GSL format
          const groupMatches = (group.matches || []).sort((a, b) => a.round_number - b.round_number || a.position - b.position);
          const completedCount = groupMatches.filter(m => m.status === 'completed').length;
          const liveCount = groupMatches.filter(isMatchLive).length;
          const groupTeams = group.standings || [];

          // Group matches by stage label
          const stages = [];
          const seenLabels = new Set();
          // Detect if this is double elim by checking for WB/LB labels
          const isDoubleElimGroup = groupMatches.some(m => (m.custom_label || '').startsWith('LB '));
          // Separate winners and losers bracket matches
          const wbMatches = isDoubleElimGroup ? groupMatches.filter(m => !(m.custom_label || '').startsWith('LB ')) : groupMatches;
          const lbMatches = isDoubleElimGroup ? groupMatches.filter(m => (m.custom_label || '').startsWith('LB ')) : [];

          // Build stages for winners bracket
          const wbStages = [];
          const wbSeen = new Set();
          for (const m of wbMatches) {
            const label = m.custom_label || `Round ${m.round_number}`;
            const stageKey = `R${m.round_number}`;
            if (!wbSeen.has(stageKey)) {
              wbSeen.add(stageKey);
              wbStages.push({ round: m.round_number, label, matches: [] });
            }
            const stage = wbStages.find(s => s.round === m.round_number);
            if (stage) stage.matches.push(m);
          }

          // Build stages for losers bracket
          const lbStages = [];
          const lbSeen = new Set();
          for (const m of lbMatches) {
            const label = m.custom_label || `Round ${m.round_number}`;
            const stageKey = `R${m.round_number}`;
            if (!lbSeen.has(stageKey)) {
              lbSeen.add(stageKey);
              lbStages.push({ round: m.round_number, label, matches: [] });
            }
            const stage = lbStages.find(s => s.round === m.round_number);
            if (stage) stage.matches.push(m);
          }

          // For non-double-elim, use flat stages
          if (!isDoubleElimGroup) {
            for (const m of groupMatches) {
              const label = m.custom_label || `Round ${m.round_number}`;
              const stageKey = `R${m.round_number}`;
              if (!seenLabels.has(stageKey)) {
                seenLabels.add(stageKey);
                stages.push({ round: m.round_number, label, matches: [] });
              }
              const stage = stages.find(s => s.round === m.round_number);
              if (stage) stage.matches.push(m);
            }
          }

          return (
            <div key={group.id || gIdx} style={{
              background: 'linear-gradient(135deg, rgba(12,12,24,0.65), rgba(12,12,24,0.5))',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid rgba(6,182,212,0.12)',
              overflow: 'hidden',
              backdropFilter: 'blur(var(--glass-blur, 20px))',
            }}>
              {/* Group Header */}
              <div style={{
                padding: '0.6rem 0.75rem',
                background: 'linear-gradient(135deg, rgba(6,182,212,0.12), transparent)',
                borderBottom: '1px solid rgba(6,182,212,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{group.name}</span>
                  <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>({groupTeams.length} teams)</span>
                  {liveCount > 0 && (
                    <span style={{
                      fontSize: '0.5rem', fontWeight: 800, color: LIVE.text,
                      background: LIVE.badge, padding: '2px 5px', borderRadius: 4,
                      border: `1px solid ${LIVE.border}`, display: 'flex', alignItems: 'center', gap: '2px',
                    }}>
                      <span style={{ width: 4, height: 4, borderRadius: '50%', background: LIVE.text, animation: 'bracketPulse 1.5s ease-in-out infinite' }} />
                      LIVE
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {!readOnly && (
                    <>
                      <select
                        value={group.group_format || 'gsl'}
                        onClick={e => e.stopPropagation()}
                        onChange={e => onUpdateGroupFormat && onUpdateGroupFormat(group.id, e.target.value)}
                        style={{
                          padding: '1px 4px', background: 'rgba(12,12,24,0.55)', border: '1px solid rgba(245,158,11,0.2)',
                          borderRadius: 4, color: '#fbbf24', fontSize: '0.55rem', fontWeight: 700, outline: 'none', cursor: 'pointer',
                        }}
                      >
                        <option value="gsl">GSL</option>
                        <option value="single_elim">Single Elim</option>
                        <option value="double_elim">Double Elim</option>
                      </select>
                      <select
                        value={group.best_of || 1}
                        onClick={e => e.stopPropagation()}
                        onChange={e => onUpdateGroupBo && onUpdateGroupBo(group.id, parseInt(e.target.value))}
                        style={{
                          padding: '1px 4px', background: 'rgba(12,12,24,0.55)', border: '1px solid rgba(6,182,212,0.2)',
                          borderRadius: 4, color: '#67e8f9', fontSize: '0.7rem', fontWeight: 700, outline: 'none', cursor: 'pointer',
                        }}
                      >
                        <option value={1}>BO1</option>
                        <option value={3}>BO3</option>
                        <option value={5}>BO5</option>
                      </select>
                    </>
                  )}
                  {groupMatches.length > 0 && (
                    <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>{completedCount}/{groupMatches.length}</span>
                  )}
                  {!readOnly && isDraft && (
                    <button onClick={() => onDeleteGroup && onDeleteGroup(group.id)} style={{
                      background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.15)',
                      color: '#fb7185', padding: '1px 5px', borderRadius: 4, cursor: 'pointer', fontSize: '0.55rem', fontWeight: 700,
                    }}>x</button>
                  )}
                </div>
              </div>

              {/* Team list (draft mode) or matches */}
              <div style={{ padding: '0.5rem 0.6rem' }}>
                {/* Teams assigned */}
                {isDraft && groupTeams.length > 0 && (
                  <div style={{ marginBottom: groupMatches.length > 0 ? '0.5rem' : 0 }}>
                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.06em' }}>Seeds</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {groupTeams.map((s, idx) => {
                        const isDragOverThis = dragOverTarget && dragOverTarget.groupId === group.id && dragOverTarget.teamId === s.team_id;
                        const isDragging = dragSource && dragSource.teamId === s.team_id;
                        const canDrag = !readOnly && isDraft;
                        return (
                        <span key={s.team_id}
                          draggable={canDrag}
                          onDragStart={canDrag ? (e) => {
                            e.stopPropagation();
                            setDragSource({ groupId: group.id, teamId: s.team_id });
                            e.dataTransfer.effectAllowed = 'move';
                          } : undefined}
                          onDragOver={canDrag ? (e) => { e.preventDefault(); e.stopPropagation(); setDragOverTarget({ groupId: group.id, teamId: s.team_id }); } : undefined}
                          onDragLeave={canDrag ? () => setDragOverTarget(null) : undefined}
                          onDrop={canDrag ? (e) => {
                            e.preventDefault(); e.stopPropagation();
                            if (dragSource && dragSource.groupId === group.id && dragSource.teamId !== s.team_id) {
                              onSwapGroupTeams && onSwapGroupTeams(group.id, dragSource.teamId, s.team_id);
                            }
                            setDragSource(null); setDragOverTarget(null);
                          } : undefined}
                          onDragEnd={() => { setDragSource(null); setDragOverTarget(null); }}
                          style={{
                          fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-primary, #f1f5f9)',
                          background: isDragOverThis ? 'rgba(6,182,212,0.25)' : 'rgba(6,182,212,0.08)',
                          padding: '2px 6px', borderRadius: 4,
                          border: isDragOverThis ? '1px dashed rgba(6,182,212,0.6)' : '1px solid rgba(6,182,212,0.12)',
                          display: 'flex', alignItems: 'center', gap: '3px',
                          cursor: canDrag ? 'grab' : 'default',
                          opacity: isDragging ? 0.5 : 1,
                          transition: 'background 0.15s ease, border 0.15s ease, opacity 0.15s ease',
                        }}>
                          {canDrag && <span style={{ fontSize: '0.5rem', color: 'var(--text-muted)', cursor: 'grab' }}>⠿</span>}
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.5rem' }}>#{idx + 1}</span>
                          {teamName(s.team_id)}
                          {!readOnly && isDraft && (
                            <>
                              <button onClick={() => onRemoveTeamFromGroup && onRemoveTeamFromGroup(group.id, s.team_id)} style={{
                              background: 'none', border: 'none', color: '#fb7185', cursor: 'pointer', fontSize: '0.7rem', padding: '0 2px', lineHeight: 1,
                            }}>x</button>
                            </>
                          )}
                        </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Add Team (draft) */}
                {!readOnly && isDraft && (
                  <div style={{ marginBottom: groupMatches.length > 0 ? '0.5rem' : 0 }}>
                    {addingTeam === group.id ? (
                      <div style={{
                        background: 'rgba(12,12,24,0.65)', border: '1px solid rgba(6,182,212,0.25)',
                        borderRadius: 'var(--radius-sm)', padding: '0.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(var(--glass-blur, 20px))',
                      }}>
                        {/* Search input */}
                        <input
                          type="text"
                          placeholder="Search teams..."
                          value={teamSearch}
                          onChange={e => setTeamSearch(e.target.value)}
                          autoFocus
                          style={{
                            width: '100%', padding: '5px 8px', fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(12,12,24,0.55)', border: '1px solid rgba(6,182,212,0.2)',
                            borderRadius: 5, color: 'var(--text-primary, #f1f5f9)', outline: 'none', marginBottom: '6px',
                            boxSizing: 'border-box',
                          }}
                        />
                        {/* Team list */}
                        <div style={{ maxHeight: '180px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {(() => {
                            const filtered = availableTeams.filter(t =>
                              !teamSearch || t.name.toLowerCase().includes(teamSearch.toLowerCase())
                            );
                            return filtered.length > 0 ? filtered.map(t => (
                              <button key={t.id} onClick={() => {
                                onAddTeamToGroup && onAddTeamToGroup(group.id, t.id);
                                // Stay open — don't close the dropdown
                              }} style={{
                                background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.12)',
                                color: 'var(--text-primary, #f1f5f9)', padding: '5px 8px', borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem',
                                fontWeight: 600, textAlign: 'left', transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => e.target.style.background = 'rgba(6,182,212,0.18)'}
                              onMouseLeave={e => e.target.style.background = 'rgba(6,182,212,0.06)'}
                              >{t.name}</button>
                            )) : (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '4px 0', textAlign: 'center' }}>
                                {teamSearch ? 'No matching teams' : 'No available teams'}
                              </span>
                            );
                          })()}
                        </div>
                        {/* Done button */}
                        <button onClick={() => { setAddingTeam(null); setTeamSearch(''); }} style={{
                          marginTop: '6px', width: '100%', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)',
                          color: '#67e8f9', padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                        }}>Done</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddingTeam(group.id)} style={{
                        background: 'rgba(6,182,212,0.06)', border: '1px dashed rgba(6,182,212,0.2)',
                        color: '#22d3ee', padding: '3px 6px', borderRadius: 4, cursor: 'pointer', fontSize: '0.68rem',
                        fontWeight: 600, width: '100%',
                      }}>+ Add Team</button>
                    )}
                  </div>
                )}

                {/* Bracket-style layout: rounds as columns, matches stacked vertically */}
                {/* Non-double-elim: single bracket */}
                {!isDoubleElimGroup && stages.length > 0 && (
                  <div style={{
                    display: 'flex', gap: '0.75rem', overflowX: 'auto',
                    paddingBottom: '0.25rem',
                  }}>
                    {stages.map((stage, sIdx) => (
                      <div key={sIdx} style={{
                        flex: '0 0 auto',
                        minWidth: '220px',
                        display: 'flex', flexDirection: 'column',
                      }}>
                        <div style={{
                          fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                          textAlign: 'center', marginBottom: '0.5rem',
                          padding: '5px 8px',
                          background: 'rgba(6,182,212,0.06)',
                          borderRadius: 'var(--radius-sm, 6px)',
                          border: '1px solid rgba(6,182,212,0.08)',
                          fontFamily: 'var(--font-display)',
                        }}>
                          {stage.label}
                        </div>
                        <div style={{
                          display: 'flex', flexDirection: 'column',
                          justifyContent: 'space-around',
                          flex: 1, gap: '0.5rem',
                        }}>
                          {stage.matches.map(m => renderMatchCard(m))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Double elim: Upper Bracket + Lower Bracket separated */}
                {isDoubleElimGroup && (wbStages.length > 0 || lbStages.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Upper Bracket */}
                    {wbStages.length > 0 && (
                      <div>
                        <div style={{
                          fontSize: '0.7rem', fontWeight: 800, color: '#34d399',
                          textTransform: 'uppercase', letterSpacing: '0.08em',
                          marginBottom: '0.5rem', padding: '4px 10px',
                          background: 'rgba(34,197,94,0.08)',
                          border: '1px solid rgba(34,197,94,0.15)',
                          borderRadius: 'var(--radius-sm, 6px)', display: 'inline-block',
                          fontFamily: 'var(--font-display)',
                        }}>
                          Upper Bracket
                        </div>
                        <div style={{
                          display: 'flex', gap: '0.75rem', overflowX: 'auto',
                          paddingBottom: '0.25rem',
                        }}>
                          {wbStages.map((stage, sIdx) => (
                            <div key={sIdx} style={{
                              flex: '0 0 auto',
                              minWidth: '220px',
                              display: 'flex', flexDirection: 'column',
                            }}>
                              <div style={{
                                fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)',
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                textAlign: 'center', marginBottom: '0.5rem',
                                padding: '5px 8px',
                                background: 'rgba(34,197,94,0.04)',
                                borderRadius: 'var(--radius-sm, 6px)',
                                border: '1px solid rgba(34,197,94,0.1)',
                                fontFamily: 'var(--font-display)',
                              }}>
                                {stage.label}
                              </div>
                              <div style={{
                                display: 'flex', flexDirection: 'column',
                                justifyContent: 'space-around',
                                flex: 1, gap: '0.5rem',
                              }}>
                                {stage.matches.map(m => renderMatchCard(m))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Divider */}
                    {wbStages.length > 0 && lbStages.length > 0 && (
                      <div style={{
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, var(--glass-border, rgba(255,255,255,0.07)), transparent)',
                      }} />
                    )}

                    {/* Lower Bracket */}
                    {lbStages.length > 0 && (
                      <div>
                        <div style={{
                          fontSize: '0.7rem', fontWeight: 800, color: '#fb7185',
                          textTransform: 'uppercase', letterSpacing: '0.08em',
                          marginBottom: '0.5rem', padding: '4px 10px',
                          background: 'rgba(244,63,94,0.08)',
                          border: '1px solid rgba(244,63,94,0.15)',
                          borderRadius: 'var(--radius-sm, 6px)', display: 'inline-block',
                          fontFamily: 'var(--font-display)',
                        }}>
                          Lower Bracket
                        </div>
                        <div style={{
                          display: 'flex', gap: '0.75rem', overflowX: 'auto',
                          paddingBottom: '0.25rem',
                        }}>
                          {lbStages.map((stage, sIdx) => (
                            <div key={sIdx} style={{
                              flex: '0 0 auto',
                              minWidth: '220px',
                              display: 'flex', flexDirection: 'column',
                            }}>
                              <div style={{
                                fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)',
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                textAlign: 'center', marginBottom: '0.5rem',
                                padding: '5px 8px',
                                background: 'rgba(244,63,94,0.04)',
                                borderRadius: 'var(--radius-sm, 6px)',
                                border: '1px solid rgba(244,63,94,0.1)',
                                fontFamily: 'var(--font-display)',
                              }}>
                                {stage.label}
                              </div>
                              <div style={{
                                display: 'flex', flexDirection: 'column',
                                justifyContent: 'space-around',
                                flex: 1, gap: '0.5rem',
                              }}>
                                {stage.matches.map(m => renderMatchCard(m))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Move Team Modal */}
      {movingTeam && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
        }} onClick={() => setMovingTeam(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'rgba(8,8,18,0.95)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
            boxShadow: '0 32px 80px rgba(0,0,0,0.7)', padding: '1.25rem 1.5rem', width: '100%', maxWidth: '340px',
            backdropFilter: 'blur(var(--glass-blur, 20px))',
          }}>
            <h3 style={{ margin: '0 0 0.75rem', color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>
              Move {teamName(movingTeam.teamId)}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {groups.filter(g => g.id !== movingTeam.fromGroupId).map(g => (
                <button key={g.id} onClick={async () => {
                  if (onRemoveTeamFromGroup) await onRemoveTeamFromGroup(movingTeam.fromGroupId, movingTeam.teamId);
                  if (onAddTeamToGroup) await onAddTeamToGroup(g.id, movingTeam.teamId);
                  setMovingTeam(null);
                }} style={{
                  background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.15)',
                  color: 'var(--text-primary, #f1f5f9)', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600, textAlign: 'left',
                }}>
                  → {g.name}
                </button>
              ))}
            </div>
            <button onClick={() => setMovingTeam(null)} style={{
              marginTop: '0.75rem', width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
              color: 'var(--text-secondary)', padding: '0.4rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.72rem',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
        }} onClick={() => setScheduleModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'rgba(8,8,18,0.95)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
            boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(6,182,212,0.08)',
            width: '100%', maxWidth: '440px', overflow: 'hidden',
            backdropFilter: 'blur(var(--glass-blur, 20px))',
          }}>
            <div style={{
              padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
              background: 'linear-gradient(135deg, rgba(6,182,212,0.08), transparent)',
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>Schedule Match</h3>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                {teamName(scheduleModal.team1_id)} <span style={{ color: 'var(--text-muted)' }}>vs</span> {teamName(scheduleModal.team2_id)}
                <span style={{
                  marginLeft: '0.5rem', color: '#67e8f9', fontWeight: 700,
                  background: 'rgba(6,182,212,0.1)', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem',
                }}>BO{scheduleModal.best_of}</span>
              </div>
            </div>
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Select Server
              </label>
              <select value={schedServerId} onChange={e => setSchedServerId(e.target.value)} style={{
                width: '100%', padding: '0.6rem 0.75rem', background: 'rgba(12,12,24,0.55)', border: '1px solid var(--glass-border, rgba(255,255,255,0.07))',
                borderRadius: 'var(--radius-md)', color: 'var(--text-primary, #f1f5f9)', fontSize: '0.85rem', outline: 'none',
              }}>
                <option value="">Select server...</option>
                {(servers || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.ip}:{s.port})</option>
                ))}
              </select>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.75rem', lineHeight: 1.5 }}>
                Map will be selected on the match page after scheduling.
              </div>
            </div>
            <div style={{
              padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.2)',
            }}>
              <button onClick={() => setScheduleModal(null)} style={{
                padding: '0.5rem 1.25rem', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8',
              }}>Cancel</button>
              <button onClick={() => {
                if (!schedServerId) return;
                onScheduleMatch(scheduleModal.id, parseInt(schedServerId), null);
                setScheduleModal(null);
                setSchedServerId('');
              }} style={{
                padding: '0.5rem 1.5rem', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                background: 'var(--accent-gradient)', border: 'none', color: '#fff',
                boxShadow: '0 4px 14px rgba(6,182,212,0.35)',
                opacity: schedServerId ? 1 : 0.4,
              }}>Schedule Match</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupStageView;
